import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import BrandMark from "../components/BrandMark";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate(result.data.role === "admin" ? "/admin" : "/client");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-[#0f0f10] relative items-center justify-center">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1770513649465-2c60c8039806?w=1200&h=900&fit=crop" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 px-16 max-w-lg">
          <BrandMark className="h-12 mb-8" />
          <h2 className="text-4xl font-black text-white tracking-tight mb-4">Welcome Back</h2>
          <p className="text-gray-400 leading-relaxed">Sign in to access your FIT FUSION ANALYTICS workspace.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors" data-testid="login-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <div className="lg:hidden mb-8">
            <BrandMark className="h-9" />
          </div>

          <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="login-heading">Sign In</h1>
          <p className="text-muted-foreground text-sm mb-8">Access your training portal</p>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 mb-6 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com" className="mt-1.5 rounded-none bg-card border-border" required data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password" className="rounded-none bg-card border-border pr-10" required data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none py-5 font-bold tracking-wide" data-testid="login-submit-button">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            <div className="text-center">
              <Link to="/reset-password" className="text-xs text-muted-foreground hover:text-[#007AFF] transition-colors" data-testid="login-forgot-password-link">
                Forgot your password?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
